import { createSlice } from '@reduxjs/toolkit'
import {
    fetchOrderByNumber,
    submitOrder,
    submitMemberOrder,
    cancelProduct,
    fetchOrderById,
    fetchAvailablePromos,
} from '../thunk/orderThunk'

const initialState = {
    // status
    isLoading: false,
    error: null,

    // data
    completeOrder: null,
    searchedOrder: null,
    availablePromos: [],
    isPromoLoading: false,
    promoError: null,

    // Timestamp to trigger promo eligibility recalculation
    // Updated when cart modal opens to ensure time-based promos are checked with current time
    promoCheckTimestamp: Date.now(),
}

export const orderSlice = createSlice({
    name: 'order',
    initialState,
    reducers: {
        setCompleteOrder: (state, action) => {
            state.completeOrder = action.payload
        },
        setSearchedOrder: (state, action) => {
            state.searchedOrder = action.payload
        },
        updateOrderPaid: (state, action) => {
            const paidOrder = action.payload

            if (!state.searchedOrder) return
            state.searchedOrder = {
                ...state.searchedOrder,
                ...paidOrder,
            }
        },
        updateCompletedDish: (state, action) => {
            const { dish } = action.payload

            if (!state.searchedOrder || !state.searchedOrder.dishes) return

            const dishes = state.searchedOrder.dishes.map(item => {
                if (item.id === dish.id) {
                    const variant = item.variants.find(v => v.variantId === dish.variantId)
                    if (variant) {
                        variant.completedAt = dish.completedAt
                        if (dish.pickupSlotNumber !== undefined) {
                            variant.pickupSlotNumber = dish.pickupSlotNumber
                        }
                    }
                }
                return item
            })

            state.searchedOrder = {
                ...state.searchedOrder,
                dishes,
            }
        },
        // Update timestamp to trigger promo eligibility recalculation
        updatePromoCheckTimestamp: state => {
            state.promoCheckTimestamp = Date.now()
        },
    },
    extraReducers: builder => {
        builder
            /** fetchOrderByNumber */
            .addCase(fetchOrderByNumber.pending, state => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchOrderByNumber.fulfilled, (state, action) => {
                state.isLoading = false
                state.searchedOrder = action.payload
            })
            .addCase(fetchOrderByNumber.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload
            })
            /** submitOrder */
            .addCase(submitOrder.pending, state => {
                state.isLoading = true
                state.error = null
            })
            .addCase(submitOrder.fulfilled, (state, action) => {
                state.isLoading = false
                state.completeOrder = action.payload
            })
            .addCase(submitOrder.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload
            })
            /** submitMemberOrder */
            .addCase(submitMemberOrder.pending, state => {
                state.isLoading = true
                state.error = null
            })
            .addCase(submitMemberOrder.fulfilled, (state, action) => {
                state.isLoading = false
                state.completeOrder = action.payload
            })
            .addCase(submitMemberOrder.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload
            })
            /** cancelProduct */
            .addCase(cancelProduct.pending, state => {
                state.isLoading = true
                state.error = null
            })
            .addCase(cancelProduct.fulfilled, (state, action) => {
                // const { result, productId } = action.payload

                state.isLoading = false
            })
            .addCase(cancelProduct.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload
            })
            /** fetchOrderById */
            .addCase(fetchOrderById.pending, state => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchOrderById.fulfilled, (state, action) => {
                state.isLoading = false
                state.completeOrder = action.payload
            })
            .addCase(fetchOrderById.rejected, state => {
                state.isLoading = false
            })
            /** fetchAvailablePromos */
            .addCase(fetchAvailablePromos.pending, state => {
                state.isPromoLoading = true
                state.promoError = null
            })
            .addCase(fetchAvailablePromos.fulfilled, (state, action) => {
                state.isPromoLoading = false
                state.availablePromos = action.payload
            })
            .addCase(fetchAvailablePromos.rejected, (state, action) => {
                state.isPromoLoading = false
                state.promoError = action.payload
            })
    },
})

export const orderActions = {
    ...orderSlice.actions,
    fetchOrderByNumber,
    submitOrder,
    submitMemberOrder,
    cancelProduct,
    fetchOrderById,
    fetchAvailablePromos,
}

export default orderSlice.reducer
